import { useEffect, useMemo, useState } from 'react';
import type { Exercise, LoadMode, Profile, TemplateExercise, WorkoutTemplate } from '../shared/types';
import { buildGroups } from '../shared/engine';
import { api } from './api';
import { Card, ErrorNotice, Field, Spinner, SuccessNotice } from './components';

interface CustomExerciseDraft {
  name: string;
  equipment: string;
  muscles: string;
  loadMode: LoadMode;
  description: string;
  cues: string;
  mistakes: string;
  videoUrl: string;
  videoSourceName: string;
  defaultIncrement: string;
  loadOptions: string;
  isUnilateral: boolean;
}

const emptyExercise: CustomExerciseDraft = {
  name: '', equipment: '', muscles: '', loadMode: 'stack_kg', description: '', cues: '', mistakes: '',
  videoUrl: '', videoSourceName: '', defaultIncrement: '5', loadOptions: '5, 10, 15, 20, 25, 30', isUnilateral: false,
};

export function Routine({ profile }: { profile: Profile }) {
  const [templates, setTemplates] = useState<WorkoutTemplate[] | null>(null);
  const [selected, setSelected] = useState(0);
  const [draft, setDraft] = useState<WorkoutTemplate | null>(null);
  const [library, setLibrary] = useState<Exercise[]>([]);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [editingProfileId,setEditingProfileId]=useState(profile.id);
  const [newWorkout, setNewWorkout] = useState({ code: '', name: '', subtitle: '' });
  const [customExercise, setCustomExercise] = useState<CustomExerciseDraft>(emptyExercise);
  const [videoEdit, setVideoEdit] = useState({ exerciseId: '', videoUrl: '', videoSourceName: '',loadOptions:'' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  async function load(preferredTemplateId?: string) {
    try {
      const list = await api<WorkoutTemplate[]>(`/routines?profileId=${encodeURIComponent(editingProfileId)}`);
      const index = preferredTemplateId ? Math.max(0, list.findIndex((item) => item.id === preferredTemplateId)) : Math.min(selected, Math.max(0, list.length - 1));
      setTemplates(list);
      setSelected(index);
      setDraft(structuredClone(list[index] ?? null));
      if (profile.isAdmin) setProfiles(await api<Profile[]>('/profiles'));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load routines');
    }
  }

  async function loadLibrary() {
    setLibrary(await api<Exercise[]>('/exercises'));
  }

  useEffect(() => {
    void load();
    void loadLibrary().catch(() => undefined);
  }, [editingProfileId]);

  useEffect(() => {
    if (templates) setDraft(structuredClone(templates[selected] ?? null));
  }, [selected]);

  const groups = useMemo(() => draft ? buildGroups(draft) : [], [draft]);

  function updateItem(id: string, patch: Partial<TemplateExercise>) {
    if (draft) setDraft({ ...draft, exercises: draft.exercises.map((item) => item.id === id ? { ...item, ...patch } : item) });
  }

  function remove(id: string) {
    if (draft) setDraft({ ...draft, exercises: draft.exercises.filter((item) => item.id !== id).map((item, index) => ({ ...item, sortOrder: index + 1 })) });
  }

  function move(id: string, direction: -1 | 1) {
    if (!draft) return;
    const items = [...draft.exercises].sort((a, b) => a.sortOrder - b.sortOrder);
    const index = items.findIndex((item) => item.id === id);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= items.length) return;
    [items[index], items[target]] = [items[target], items[index]];
    setDraft({ ...draft, exercises: items.map((item, itemIndex) => ({ ...item, sortOrder: itemIndex + 1 })) });
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setError('');
    try {
      await api(`/routines/${draft.id}`, { method: 'PATCH', body: JSON.stringify({ name: draft.name, subtitle: draft.subtitle, rotationOrder:draft.rotationOrder,version: draft.version ?? 1 }) });
      await api(`/routines/${draft.id}/exercises`, {
        method: 'PUT',
        body: JSON.stringify({
          version: (draft.version ?? 1) + 1,
          exercises: draft.exercises.map((item) => ({
            exerciseId: item.exercise.id, supersetGroup: item.supersetGroup, positionInPair: item.positionInPair,
            sortOrder: item.sortOrder, sets: item.sets, repMin: item.repMin, repMax: item.repMax,
            restSeconds: item.restSeconds, targetCount: item.targetCount, notes: item.notes,
          })),
        }),
      });
      setSuccess('Routine saved. Workout history was not changed.');
      await load(draft.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save routine');
    } finally {
      setBusy(false);
    }
  }

  function addExercise(exerciseId: string) {
    if (!draft) return;
    const selectedExercise = library.find((item) => item.id === exerciseId);
    if (!selectedExercise) return;
    const group = String.fromCharCode(65 + groups.length);
    const item: TemplateExercise = {
      id: crypto.randomUUID(), exercise: selectedExercise, supersetGroup: group, positionInPair: 1,
      sortOrder: draft.exercises.length + 1, sets: 3, repMin: 8, repMax: 12, restSeconds: 60,
      targetCount: null, notes: '',
    };
    setDraft({ ...draft, exercises: [...draft.exercises, item] });
  }

  async function createWorkout() {
    setBusy(true);
    setError('');
    try {
      const created = await api<{ id: string }>('/routines', { method: 'POST', body: JSON.stringify({...newWorkout,profileId:editingProfileId}) });
      setNewWorkout({ code: '', name: '', subtitle: '' });
      setSuccess('New workout created. Add exercises, then save it.');
      await load(created.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create workout');
    } finally {
      setBusy(false);
    }
  }

  async function duplicateWorkout() {
    if (!draft) return;
    setBusy(true);
    setError('');
    try {
      const created = await api<{ id: string }>(`/routines/${draft.id}/duplicate`, { method: 'POST' });
      setSuccess('Workout duplicated. Its copy is independent of the original.');
      await load(created.id);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not duplicate workout');
    } finally {
      setBusy(false);
    }
  }

  async function deleteWorkout() {
    if (!draft || !window.confirm(`Delete ${draft.name}? Completed workout history will remain.`)) return;
    setBusy(true);
    setError('');
    try {
      await api(`/routines/${draft.id}`, { method: 'DELETE' });
      setSuccess('Workout removed. Completed workout history was kept.');
      setSelected(0);
      await load();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not delete workout');
    } finally {
      setBusy(false);
    }
  }

  async function createCustomExercise() {
    setBusy(true);
    setError('');
    try {
      const created = await api<{ id: string }>('/exercises', {
        method: 'POST',
        body: JSON.stringify({
          name: customExercise.name, equipment: customExercise.equipment,
          primaryMuscles: customExercise.muscles.split(',').map((value) => value.trim()).filter(Boolean),
          loadMode: customExercise.loadMode, description: customExercise.description,
          cues: customExercise.cues.split(',').map((value) => value.trim()).filter(Boolean),
          commonMistakes: customExercise.mistakes.split(',').map((value) => value.trim()).filter(Boolean),
          videoUrl: customExercise.videoUrl, videoSourceName: customExercise.videoSourceName,
          defaultIncrement: customExercise.defaultIncrement ? Number(customExercise.defaultIncrement) : null,
          loadOptions:customExercise.loadOptions.split(',').map(value=>Number(value.trim())).filter(value=>Number.isFinite(value)&&value>=0),
          isUnilateral: customExercise.isUnilateral,
        }),
      });
      await loadLibrary();
      setCustomExercise(emptyExercise);
      setSuccess('Custom exercise added to the library. Select it from the library to add it to this workout.');
      document.getElementById(`exercise-option-${created.id}`)?.scrollIntoView({ block: 'center' });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not create exercise');
    } finally {
      setBusy(false);
    }
  }

  async function updateExerciseVideo() {
    if (!videoEdit.exerciseId) return;
    setBusy(true);
    setError('');
    try {
      await api(`/exercises/${videoEdit.exerciseId}/video`, { method: 'PATCH', body: JSON.stringify({...videoEdit,loadOptions:videoEdit.loadOptions.split(',').map(value=>Number(value.trim())).filter(value=>Number.isFinite(value)&&value>=0)}) });
      await loadLibrary();
      setSuccess('Exercise demonstration link updated without a deployment.');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update the demonstration link');
    } finally {
      setBusy(false);
    }
  }

  if (!templates && !error) return <Spinner label="Loading routines" />;

  return <>
    <header className="page-heading">
      <p className="eyebrow">Editable per profile</p>
      <h1>Routine</h1>
      <p>The seeded A–D workouts live in the database and remain editable without changing completed history.</p>
    </header>
    {error && <ErrorNotice message={error} onDismiss={() => setError('')} />}
    {success && <SuccessNotice>{success}</SuccessNotice>}
    {profile.isAdmin&&profiles.length>1&&<Card><Field label="Edit routines for"><select value={editingProfileId} onChange={event=>{setEditingProfileId(event.target.value);setSelected(0);}}>{profiles.map(target=><option key={target.id} value={target.id}>{target.displayName}</option>)}</select></Field></Card>}

    <details className="card routine-create">
      <summary>Create a workout</summary>
      <div className="two-cols">
        <Field label="Short code"><input maxLength={8} value={newWorkout.code} onChange={(event) => setNewWorkout({ ...newWorkout, code: event.target.value.toUpperCase() })} placeholder="E" /></Field>
        <Field label="Workout name"><input value={newWorkout.name} onChange={(event) => setNewWorkout({ ...newWorkout, name: event.target.value })} placeholder="Workout E" /></Field>
      </div>
      <Field label="Focus"><input value={newWorkout.subtitle} onChange={(event) => setNewWorkout({ ...newWorkout, subtitle: event.target.value })} placeholder="Training focus" /></Field>
      <button className="button button--secondary" onClick={() => void createWorkout()} disabled={busy || !newWorkout.code || !newWorkout.name}>Create workout</button>
    </details>

    <div className="workout-tabs" role="tablist" aria-label="Workout templates">
      {templates?.map((template, index) => <button key={template.id} role="tab" aria-selected={selected === index} onClick={() => setSelected(index)}>{template.code}</button>)}
    </div>

    {draft && <>
      <Card>
        <Field label="Workout name"><input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} /></Field>
        <Field label="Focus"><textarea value={draft.subtitle} onChange={(event) => setDraft({ ...draft, subtitle: event.target.value })} /></Field>
        <Field label="Rotation order"><input type="number" min="1" max="99" value={draft.rotationOrder} onChange={event=>setDraft({...draft,rotationOrder:Number(event.target.value)})}/></Field>
        <div className="inline-actions routine-management">
          <button className="button button--secondary" onClick={() => void duplicateWorkout()} disabled={busy}>Duplicate</button>
          <button className="button--danger-ghost" onClick={() => void deleteWorkout()} disabled={busy}>Delete</button>
        </div>
      </Card>

      <div className="routine-list">
        {groups.map((group) => <Card key={group.key}>
          <div className="routine-group-header"><span className="pair-badge">{group.key}</span><strong>{group.exercises.length > 1 ? 'Exercise group' : 'Single exercise'}</strong><span>60 sec between sets</span></div>
          {group.exercises.map((item) => <div className="routine-item" key={item.id}>
            <div className="routine-item__title"><span>{group.key}{item.positionInPair}</span><strong>{item.exercise.name}</strong></div>
            <div className="routine-controls">
              <label>Display group<input value={item.supersetGroup} maxLength={20} onChange={event=>updateItem(item.id,{supersetGroup:event.target.value.toUpperCase()})}/></label>
              <label>Position in group<input type="number" min="1" max="4" value={item.positionInPair} onChange={event=>updateItem(item.id,{positionInPair:Number(event.target.value)})}/></label>
              <label>Sets<input type="number" min="1" max="20" value={item.sets} onChange={(event) => updateItem(item.id, { sets: Number(event.target.value) })} /></label>
              <label>Min reps<input type="number" min="0" value={item.repMin ?? ''} onChange={(event) => updateItem(item.id, { repMin: event.target.value ? Number(event.target.value) : null })} /></label>
              <label>Max reps<input type="number" min="1" value={item.repMax ?? ''} onChange={(event) => updateItem(item.id, { repMax: event.target.value ? Number(event.target.value) : null })} /></label>
            </div>
            <Field label="Notes"><input value={item.notes} onChange={(event) => updateItem(item.id, { notes: event.target.value })} /></Field>
            <div className="inline-actions">
              <button onClick={() => move(item.id, -1)} aria-label={`Move ${item.exercise.name} up`}>↑</button>
              <button onClick={() => move(item.id, 1)} aria-label={`Move ${item.exercise.name} down`}>↓</button>
              <button className="button--danger-ghost" onClick={() => remove(item.id)}>Remove</button>
            </div>
          </div>)}
        </Card>)}
      </div>

      <details className="card routine-create"><summary>Preview exact workout</summary><p><strong>Workout {draft.code} · {draft.name}</strong></p><p className="muted">{draft.subtitle}</p><p>The displayed order is a guide. During a workout, choose any unfinished exercise; after its first set, finish that exercise’s remaining sets before choosing another. A 60-second rest runs only between sets of that exercise.</p><ol className="routine-preview">{groups.flatMap(group=>group.exercises.map(entry=><li key={entry.id}><strong>{group.key}{entry.positionInPair} · {entry.exercise.name}</strong><span>{entry.sets} sets · {entry.targetCount?`${entry.targetCount} count`:`${entry.repMin??'—'}–${entry.repMax??'—'} reps`} · 60 sec between its sets</span>{entry.exercise.loadOptions.length>0&&<small>Loads: {entry.exercise.loadOptions.join(', ')} kg</small>}</li>))}</ol></details>

      <Card>
        <h2>Add exercise</h2>
        <select aria-label="Exercise library" defaultValue="" onChange={(event) => { if (event.target.value) addExercise(event.target.value); event.target.value = ''; }}>
          <option value="" disabled>Choose from exercise library…</option>
          {library.map((exercise) => <option id={`exercise-option-${exercise.id}`} key={exercise.id} value={exercise.id}>{exercise.name}</option>)}
        </select>
      </Card>

      <details className="card routine-create">
        <summary>Create a custom exercise</summary>
        <Field label="Exercise name"><input value={customExercise.name} onChange={(event) => setCustomExercise({ ...customExercise, name: event.target.value })} /></Field>
        <div className="two-cols">
          <Field label="Equipment"><input value={customExercise.equipment} onChange={(event) => setCustomExercise({ ...customExercise, equipment: event.target.value })} /></Field>
          <Field label="Load type"><select value={customExercise.loadMode} onChange={(event) => setCustomExercise({ ...customExercise, loadMode: event.target.value as LoadMode })}><option value="stack_kg">Machine/cable kg</option><option value="kg_each">Kg each</option><option value="kg_total">Kg total</option><option value="bodyweight">Bodyweight</option><option value="count">Count</option></select></Field>
        </div>
        <Field label="Primary muscles" hint="Separate with commas"><input value={customExercise.muscles} onChange={(event) => setCustomExercise({ ...customExercise, muscles: event.target.value })} /></Field>
        <Field label="Technique"><textarea value={customExercise.description} onChange={(event) => setCustomExercise({ ...customExercise, description: event.target.value })} /></Field>
        <Field label="Cues" hint="Separate with commas"><input value={customExercise.cues} onChange={(event) => setCustomExercise({ ...customExercise, cues: event.target.value })} /></Field>
        <Field label="Common mistakes" hint="Separate with commas"><input value={customExercise.mistakes} onChange={(event) => setCustomExercise({ ...customExercise, mistakes: event.target.value })} /></Field>
        <Field label="Direct YouTube demonstration URL"><input type="url" inputMode="url" value={customExercise.videoUrl} onChange={(event) => setCustomExercise({ ...customExercise, videoUrl: event.target.value })} /></Field>
        <div className="two-cols">
          <Field label="Video channel"><input value={customExercise.videoSourceName} onChange={(event) => setCustomExercise({ ...customExercise, videoSourceName: event.target.value })} /></Field>
          <Field label="Weight increment"><input type="number" min="0" step="0.5" value={customExercise.defaultIncrement} onChange={(event) => setCustomExercise({ ...customExercise, defaultIncrement: event.target.value })} /></Field>
        </div>
        <Field label="Ordered load options" hint="Kilograms, separated by commas"><input value={customExercise.loadOptions} onChange={event=>setCustomExercise({...customExercise,loadOptions:event.target.value})}/></Field>
        <label className="toggle"><input type="checkbox" checked={customExercise.isUnilateral} onChange={(event) => setCustomExercise({ ...customExercise, isUnilateral: event.target.checked })} /> Record per side</label>
        <button className="button button--secondary" onClick={() => void createCustomExercise()} disabled={busy}>Add custom exercise</button>
      </details>

      {profile.isAdmin && <details className="card routine-create">
        <summary>Replace an exercise video</summary>
        <Field label="Exercise">
          <select value={videoEdit.exerciseId} onChange={(event) => {
            const selectedExercise = library.find((item) => item.id === event.target.value);
            setVideoEdit({ exerciseId: event.target.value, videoUrl: selectedExercise?.videoUrl ?? '', videoSourceName: selectedExercise?.videoSourceName ?? '',loadOptions:selectedExercise?.loadOptions.join(', ')??'' });
          }}>
            <option value="">Choose an exercise…</option>
            {library.map((exercise) => <option key={exercise.id} value={exercise.id}>{exercise.name}</option>)}
          </select>
        </Field>
        <Field label="Direct YouTube demonstration URL"><input type="url" inputMode="url" value={videoEdit.videoUrl} onChange={(event) => setVideoEdit({ ...videoEdit, videoUrl: event.target.value })} /></Field>
        <Field label="Video channel"><input value={videoEdit.videoSourceName} onChange={(event) => setVideoEdit({ ...videoEdit, videoSourceName: event.target.value })} /></Field>
        <Field label="Ordered load options" hint="Kilograms, separated by commas"><input value={videoEdit.loadOptions} onChange={event=>setVideoEdit({...videoEdit,loadOptions:event.target.value})}/></Field>
        <button className="button button--secondary" onClick={() => void updateExerciseVideo()} disabled={busy || !videoEdit.exerciseId || !videoEdit.videoUrl || !videoEdit.videoSourceName}>Update video</button>
      </details>}

      {profile.isAdmin && profiles.length > 1 && <Card>
        <h2>Copy routine</h2>
        <p className="muted">Copies this saved template only. It never copies or overwrites workout history.</p>
        {profiles.filter((target) => target.id !== editingProfileId).map((target) => <button key={target.id} className="button button--secondary" onClick={async () => {
          try {
            await api(`/routines/${draft.id}/copy`, { method: 'POST', body: JSON.stringify({ targetProfileId: target.id }) });
            setSuccess(`Routine copied to ${target.displayName}.`);
          } catch (caught) {
            setError(caught instanceof Error ? caught.message : 'Could not copy routine');
          }
        }}>Copy to {target.displayName}</button>)}
      </Card>}

      <button className="button button--primary button--full" onClick={() => void save()} disabled={busy}>{busy ? 'Saving…' : 'Save routine'}</button>
    </>}
  </>;
}
